import React, {useEffect, useRef, useState} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {
  adminSupportConversationsCache,
  adminSupportMessagesCache,
} from '../../../common/constants/queryCacheKey';
import {useGuardedMutate} from '../../../common/hooks/useGuardedMutate';
import {
  fetchSupportConversations,
  fetchSupportMessages,
  markSupportConversationRead,
  resolveSupportConversation,
  sendSupportReply,
  SupportConversation,
} from '../../../common/services/support';
import {roleLabels} from '../../../common/services/users';
import {
  badgeStyle,
  chipStyle,
  disabledButtonStyle,
  errorStyle,
  inputStyle,
  messageStyle,
  panelStyle,
  primaryButtonStyle,
  screenStyle,
  secondaryButtonStyle,
  titleStyle,
} from '../../admin/adminStyles';

const PAGE_SIZE = 30;
const MESSAGES_STEP = 50;
const MAX_MESSAGES = 500;
const MAX_REPLY_LENGTH = 4000;
const LIST_REFRESH_MS = 15000;
const THREAD_REFRESH_MS = 5000;

// The server doesn't translate, so the admin picks the user's language.
const CLOSING_MESSAGES = {
  RO: 'Am marcat problema ca rezolvată. Dacă mai ai nevoie de ajutor, scrie-ne aici.',
  EN: "We've marked this as resolved. If you need anything else, write to us here.",
  PT: 'Marcámos o problema como resolvido. Se precisares de mais ajuda, escreve-nos aqui.',
};

type ClosingLanguage = keyof typeof CLOSING_MESSAGES;

const closingLanguages = Object.keys(CLOSING_MESSAGES) as ClosingLanguage[];

const formatTime = (value: string) => new Date(value).toLocaleString();

// Below this the list and the open conversation take turns on the screen.
const narrowQuery = '(max-width: 768px)';

const useIsNarrow = () => {
  const [isNarrow, setIsNarrow] = useState(
    () => window.matchMedia(narrowQuery).matches,
  );

  useEffect(() => {
    const media = window.matchMedia(narrowQuery);
    const onChange = () => setIsNarrow(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  return isNarrow;
};

const ConversationRow: React.FC<{
  conversation: SupportConversation;
  isSelected: boolean;
  onSelect: () => void;
}> = ({conversation, isSelected, onSelect}) => {
  const isUnread = conversation.unreadCount > 0;

  return (
    <button
      onClick={onSelect}
      style={{
        ...rowStyle,
        backgroundColor: isSelected ? '#333' : 'transparent',
      }}>
      <span style={rowTopStyle}>
        <span style={{...rowNameStyle, fontWeight: isUnread ? 'bold' : 500}}>
          {conversation.fullName}
        </span>
        {isUnread ? (
          <span style={unreadBadgeStyle}>{conversation.unreadCount}</span>
        ) : null}
      </span>
      {conversation.email ? (
        <span style={rowEmailStyle}>{conversation.email}</span>
      ) : null}
      <span style={{...rowPreviewStyle, color: isUnread ? '#fff' : '#999'}}>
        {conversation.lastMessageFromSupport ? 'You: ' : ''}
        {conversation.lastMessagePreview}
      </span>
      <span style={rowTimeStyle}>
        {formatTime(conversation.lastMessageAtUtc)}
      </span>
    </button>
  );
};

const ResolvePanel: React.FC<{
  conversationId: string;
  onCancel: () => void;
  onResolved: () => void;
}> = ({conversationId, onCancel, onResolved}) => {
  const [language, setLanguage] = useState<ClosingLanguage>('RO');
  const [body, setBody] = useState(CLOSING_MESSAGES.RO);

  const resolve = useMutation({
    mutationFn: () => resolveSupportConversation(conversationId, body.trim()),
    onSuccess: onResolved,
  });
  const submit = useGuardedMutate(resolve.mutate);
  const canResolve = body.trim().length > 0 && !resolve.isPending;

  return (
    <div style={resolvePanelStyle}>
      <div style={resolveHeaderStyle}>
        <span style={threadNameStyle}>Closing message</span>
        <span style={languagesStyle}>
          {closingLanguages.map(item => (
            <button
              key={item}
              onClick={() => {
                setLanguage(item);
                setBody(CLOSING_MESSAGES[item]);
              }}
              style={chipStyle(item === language)}>
              {item}
            </button>
          ))}
        </span>
      </div>
      {resolve.error ? (
        <p style={errorStyle}>{(resolve.error as Error).message}</p>
      ) : null}
      <textarea
        value={body}
        maxLength={MAX_REPLY_LENGTH}
        onChange={event => setBody(event.target.value)}
        style={composerInputStyle}
      />
      <div style={resolveActionsStyle}>
        <button onClick={onCancel} style={secondaryButtonStyle}>
          Cancel
        </button>
        <button
          onClick={() => {
            if (canResolve) {
              submit(undefined);
            }
          }}
          disabled={!canResolve}
          style={canResolve ? primaryButtonStyle : disabledButtonStyle}>
          Send and resolve
        </button>
      </div>
    </div>
  );
};

const Thread: React.FC<{
  conversation: SupportConversation;
  onBack?: () => void;
  onResolved: () => void;
  onReopened: () => void;
}> = ({conversation, onBack, onResolved, onReopened}) => {
  const {conversationId, unreadCount} = conversation;
  const isResolved = Boolean(conversation.resolvedAtUtc);
  const queryClient = useQueryClient();
  const [limit, setLimit] = useState(MESSAGES_STEP);
  const [draft, setDraft] = useState('');
  const [isResolving, setIsResolving] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const {data: messages, error} = useQuery({
    queryKey: [adminSupportMessagesCache, conversationId, limit],
    queryFn: () => fetchSupportMessages(conversationId, limit),
    refetchInterval: THREAD_REFRESH_MS,
    placeholderData: previous => previous,
  });

  const refresh = () => {
    queryClient.invalidateQueries({queryKey: [adminSupportMessagesCache]});
    queryClient.invalidateQueries({
      queryKey: [adminSupportConversationsCache],
    });
  };

  const {mutate: markRead} = useMutation({
    mutationFn: () => markSupportConversationRead(conversationId),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [adminSupportConversationsCache],
      }),
  });

  // A mark that fails isn't retried until the count changes.
  useEffect(() => {
    if (unreadCount > 0) {
      markRead();
    }
  }, [conversationId, unreadCount, markRead]);

  const reply = useMutation({
    mutationFn: (body: string) => sendSupportReply(conversationId, body),
    onSuccess: () => {
      setDraft('');
      refresh();
      // A new message reopens it, so it's now under Open.
      if (isResolved) {
        onReopened();
      }
    },
  });
  const send = useGuardedMutate(reply.mutate);

  // Only a new last message scrolls: loading older ones keeps the place.
  const lastMessageId = messages?.[messages.length - 1]?.id;
  useEffect(() => {
    bottomRef.current?.scrollIntoView({block: 'end'});
  }, [lastMessageId]);

  const body = draft.trim();
  const canSend = body.length > 0 && !reply.isPending;
  const submit = () => {
    if (canSend) {
      send(body);
    }
  };

  const hasOlder =
    messages !== undefined && messages.length === limit && limit < MAX_MESSAGES;

  return (
    <section style={threadStyle}>
      <header style={threadHeaderStyle}>
        {onBack ? (
          <button onClick={onBack} style={secondaryButtonStyle}>
            Back
          </button>
        ) : null}
        {conversation.profilePhoto ? (
          <img src={conversation.profilePhoto} alt="" style={avatarStyle} />
        ) : null}
        <div style={threadPersonStyle}>
          <div style={threadNameStyle}>{conversation.fullName}</div>
          {conversation.email ? (
            <div style={rowEmailStyle}>{conversation.email}</div>
          ) : null}
        </div>
        {conversation.roleCode ? (
          <span style={badgeStyle}>{roleLabels[conversation.roleCode]}</span>
        ) : null}
        {isResolved ? (
          <span style={resolvedBadgeStyle}>Resolved</span>
        ) : (
          <button
            onClick={() => setIsResolving(true)}
            disabled={isResolving}
            style={isResolving ? disabledButtonStyle : secondaryButtonStyle}>
            Resolve
          </button>
        )}
      </header>

      <div style={messagesStyle}>
        {hasOlder ? (
          <button
            onClick={() =>
              setLimit(Math.min(limit + MESSAGES_STEP, MAX_MESSAGES))
            }
            style={{...secondaryButtonStyle, alignSelf: 'center'}}>
            Load older
          </button>
        ) : null}
        {error ? <p style={errorStyle}>{(error as Error).message}</p> : null}
        {(messages ?? []).map(message => (
          <div
            key={message.id}
            style={message.fromSupport ? supportBubbleStyle : userBubbleStyle}>
            <div style={bubbleBodyStyle}>{message.body}</div>
            <div style={bubbleTimeStyle}>
              {formatTime(message.createdAtUtc)}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {reply.error ? (
        <p style={errorStyle}>{(reply.error as Error).message}</p>
      ) : null}
      {isResolving ? (
        <ResolvePanel
          conversationId={conversationId}
          onCancel={() => setIsResolving(false)}
          onResolved={() => {
            refresh();
            onResolved();
          }}
        />
      ) : (
        <div style={composerStyle}>
          <textarea
            value={draft}
            maxLength={MAX_REPLY_LENGTH}
            placeholder={
              isResolved
                ? 'A new message reopens this conversation.'
                : 'Answer as DashOut Support. Enter sends, Shift+Enter starts a new line.'
            }
            onChange={event => setDraft(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                submit();
              }
            }}
            style={composerInputStyle}
          />
          <button
            onClick={submit}
            disabled={!canSend}
            style={canSend ? primaryButtonStyle : disabledButtonStyle}>
            Send
          </button>
        </div>
      )}
    </section>
  );
};

export const SupportScreen: React.FC = () => {
  const [showResolved, setShowResolved] = useState(false);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<SupportConversation | null>(null);
  const isNarrow = useIsNarrow();

  const {data, error, isLoading} = useQuery({
    queryKey: [adminSupportConversationsCache, showResolved, page],
    queryFn: () => fetchSupportConversations(page, PAGE_SIZE, showResolved),
    refetchInterval: LIST_REFRESH_MS,
    placeholderData: previous => previous,
  });

  // The open conversation may scroll to another page; the row keeps it open,
  // and the list keeps it up to date while it's on this one.
  const conversations = data?.items ?? [];
  const open =
    conversations.find(
      item => item.conversationId === selected?.conversationId,
    ) ?? selected;

  const showTab = (resolved: boolean) => {
    setShowResolved(resolved);
    setPage(1);
    setSelected(null);
  };

  const renderList = () => {
    if (error) {
      return <p style={errorStyle}>{(error as Error).message}</p>;
    }
    if (isLoading) {
      return <p style={messageStyle}>Loading...</p>;
    }
    if (conversations.length === 0) {
      return (
        <p style={messageStyle}>
          {showResolved ? 'Nothing resolved yet' : 'No open conversations'}
        </p>
      );
    }

    return (
      <>
        {conversations.map(conversation => (
          <ConversationRow
            key={conversation.conversationId}
            conversation={conversation}
            isSelected={conversation.conversationId === open?.conversationId}
            onSelect={() => setSelected(conversation)}
          />
        ))}
        {(data?.maxPage ?? 0) > 1 ? (
          <div style={pagerStyle}>
            <button
              onClick={() => setPage(page - 1)}
              disabled={page <= 1}
              style={page <= 1 ? disabledButtonStyle : secondaryButtonStyle}>
              Previous
            </button>
            <span style={rowEmailStyle}>
              {page} / {data?.maxPage}
            </span>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page >= (data?.maxPage ?? 0)}
              style={
                page >= (data?.maxPage ?? 0)
                  ? disabledButtonStyle
                  : secondaryButtonStyle
              }>
              Next
            </button>
          </div>
        ) : null}
      </>
    );
  };

  return (
    <div style={screenStyle}>
      <h1 style={titleStyle}>Support</h1>
      <div style={layoutStyle}>
        {!isNarrow || !open ? (
          <aside style={{...listStyle, flex: isNarrow ? 1 : '0 0 340px'}}>
            <div style={tabsStyle}>
              <button
                onClick={() => showTab(false)}
                style={chipStyle(!showResolved)}>
                Open
              </button>
              <button
                onClick={() => showTab(true)}
                style={chipStyle(showResolved)}>
                Resolved
              </button>
            </div>
            {renderList()}
          </aside>
        ) : null}
        {open ? (
          <Thread
            key={open.conversationId}
            conversation={open}
            onBack={isNarrow ? () => setSelected(null) : undefined}
            onResolved={() => setSelected(null)}
            onReopened={() => {
              setShowResolved(false);
              setPage(1);
            }}
          />
        ) : !isNarrow ? (
          <section style={{...threadStyle, justifyContent: 'center'}}>
            <p style={messageStyle}>Choose a conversation</p>
          </section>
        ) : null}
      </div>
    </div>
  );
};

const layoutStyle: React.CSSProperties = {
  display: 'flex',
  gap: '16px',
  maxWidth: '1100px',
  margin: '0 auto',
  height: 'calc(100vh - 180px)',
  minHeight: '420px',
};

const tabsStyle: React.CSSProperties = {
  display: 'flex',
  gap: '8px',
  padding: '4px 4px 10px',
};

const listStyle: React.CSSProperties = {
  ...panelStyle,
  padding: '8px',
  overflowY: 'auto',
  minWidth: 0,
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '3px',
  width: '100%',
  padding: '10px',
  border: 'none',
  borderBottom: '1px solid #333',
  borderRadius: '8px',
  textAlign: 'left',
  cursor: 'pointer',
};

const rowTopStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '8px',
};

const rowNameStyle: React.CSSProperties = {
  color: '#fff',
  fontSize: '0.95rem',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const rowEmailStyle: React.CSSProperties = {
  color: '#999',
  fontSize: '0.75rem',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const rowPreviewStyle: React.CSSProperties = {
  fontSize: '0.85rem',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const rowTimeStyle: React.CSSProperties = {
  color: '#666',
  fontSize: '0.7rem',
};

const unreadBadgeStyle: React.CSSProperties = {
  ...badgeStyle,
  backgroundColor: '#daff00',
  color: '#1a1a1a',
};

const pagerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 4px 4px',
};

const threadStyle: React.CSSProperties = {
  ...panelStyle,
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
};

const threadHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  paddingBottom: '12px',
  borderBottom: '1px solid #333',
};

const avatarStyle: React.CSSProperties = {
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  objectFit: 'cover',
  flexShrink: 0,
};

const threadPersonStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
};

const threadNameStyle: React.CSSProperties = {
  color: '#fff',
  fontWeight: 'bold',
};

const messagesStyle: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const bubbleStyle: React.CSSProperties = {
  maxWidth: '75%',
  padding: '8px 12px',
  borderRadius: '12px',
};

const userBubbleStyle: React.CSSProperties = {
  ...bubbleStyle,
  alignSelf: 'flex-start',
  backgroundColor: '#333',
  color: '#fff',
};

const supportBubbleStyle: React.CSSProperties = {
  ...bubbleStyle,
  alignSelf: 'flex-end',
  backgroundColor: '#daff00',
  color: '#1a1a1a',
};

const bubbleBodyStyle: React.CSSProperties = {
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
  fontSize: '0.9rem',
};

const bubbleTimeStyle: React.CSSProperties = {
  marginTop: '4px',
  fontSize: '0.65rem',
  opacity: 0.6,
};

const resolvedBadgeStyle: React.CSSProperties = {
  ...badgeStyle,
  backgroundColor: '#daff00',
  color: '#1a1a1a',
};

const resolvePanelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  paddingTop: '12px',
  borderTop: '1px solid #333',
};

const resolveHeaderStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '8px',
};

const languagesStyle: React.CSSProperties = {
  display: 'flex',
  gap: '6px',
};

const resolveActionsStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '8px',
};

const composerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-end',
  gap: '8px',
};

const composerInputStyle: React.CSSProperties = {
  ...inputStyle,
  height: '64px',
  padding: '10px 12px',
  resize: 'none',
  fontFamily: 'inherit',
};
